// src/components/navbar/Navbar.jsx
// ============================================
// NAVBAR - Fixed navigation with GLASS EFFECT on scroll
// ============================================

import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import LogoutConfirmModal from "../modals/LogoutConfirmModal";
import EditProfileModal from "./EditProfileModal";
import PCView from "./PCView";
import MobileView from "./MobileView";
import "./Navbar.css";

// Get API URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";
// If the frontend is running on a deployed host (not localhost) but
// the env var wasn't set (left as localhost), use the known Render backend URL.
const getApiBase = () => {
  // Known deployed backend URL (fallback)
  const FALLBACK_BACKEND = 'https://bluesense.onrender.com';

  // If API_BASE_URL points to localhost but we're on a production host, switch.
  try {
    const hostname = window.location.hostname || '';
    if ((API_BASE_URL.includes('localhost') || API_BASE_URL.includes('127.0.0.1')) 
        && hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return FALLBACK_BACKEND;
    }
  } catch (e) {
    // ignore and return API_BASE_URL
  }

  return API_BASE_URL;
};
const BASE_API = getApiBase();

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Scroll state for glass effect
  const [scrolled, setScrolled] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => {
    return location.pathname === path;
  };

  // AUTO-SCROLL TO TOP ON PAGE NAVIGATION
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [location.pathname]);

  // Detect scroll for glass effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Load user data from localStorage
  useEffect(() => {
    loadUserFromStorage();

    const handleStorageChange = () => {
      loadUserFromStorage();
    };

    // Listen for profile updates from other components
    const handleProfileUpdated = (event) => {
      if (event.detail) {
        setUserData(event.detail);
        setUserRole(event.detail.role);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleStorageChange);
    window.addEventListener("profileUpdated", handleProfileUpdated);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleStorageChange);
      window.removeEventListener("profileUpdated", handleProfileUpdated);
    };
  }, [location.pathname]);

  const loadUserFromStorage = () => {
    const token = localStorage.getItem("token");
    const userStr = localStorage.getItem("user");

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        setUserRole(user.role);
        setUserData(user);
      } catch (e) {
        console.error("Failed to parse user:", e);
        setUserRole(null);
        setUserData(null);
      }
    } else {
      setUserRole(null);
      setUserData(null);
    }
  };

  useEffect(() => {
    if (showViewModal || showEditModal) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
  }, [showViewModal, showEditModal]);

  // Clear message after 3 seconds
  useEffect(() => {
    if (saveMessage) {
      const timer = setTimeout(() => setSaveMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveMessage]);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
    setIsOpen(false);
  };

  const stopESP32 = async () => {
    try {
      await fetch("https://bluesense.onrender.com/api/readings/stop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
    } catch (err) {
      console.warn("stopESP32 failed (non-blocking):", err);
    }
  };

  const confirmLogout = async () => {
    await stopESP32();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUserRole(null);
    setUserData(null);
    setShowLogoutConfirm(false);
    setShowViewModal(false);
    setShowEditModal(false);
    navigate("/");
  };

  const getAvatarSrc = () => {
    if (!userData) return null;

    if (userData.avatar) {
      if (userData.avatar.startsWith("http")) {
        return userData.avatar;
      }
      return `${BASE_API}${userData.avatar}`;
    }

    if (userData.googleAvatar) {
      return userData.googleAvatar;
    }

    return null;
  };

  const handleEditProfile = () => {
    setShowEditModal(true);
  };

  const handleSaveFromModal = async (updatedData) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setSaveMessage('Please login again');
      return;
    }

    try {
      console.log('Saving profile data:', updatedData);
      
      const response = await axios.put(
        `${BASE_API}/api/auth/profile`,
        {
          name: updatedData.name,
          phone: updatedData.phone || "",
          address: updatedData.address || "",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      console.log('API Response:', response.data);

      if (response.data.user) {
        // Update localStorage with the response from backend
        const updatedUser = { 
          ...userData, 
          ...response.data.user
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUserData(updatedUser);
        setUserRole(updatedUser.role);

        // Close edit modal
        setShowEditModal(false);
        
        // Show success message
        setSaveMessage('Profile updated successfully!');

        // Dispatch event for other components
        window.dispatchEvent(
          new CustomEvent("profileUpdated", { detail: updatedUser }),
        );
      }
    } catch (error) {
      console.error("Failed to save profile:", error);
      const errorMsg = error.response?.data?.message || "Failed to save profile. Please try again.";
      setSaveMessage(errorMsg);
      throw new Error(errorMsg);
    }
  };

  return (
    <>
      <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="navbar-container">
          {/* LEFT - Logo */}
          <Link to="/" className="navbar-logo">
            <img
              src="/images/logo/resort-logo.png"
              alt="Catherine's Oasis"
              className="navbar-logo-img"
            />
            <span className="navbar-logo-text">Catherine's Oasis</span>
          </Link>

          {/* CENTER - Desktop Navigation with Simple Underline */}
          <div className="nav-center desktop-only">
            <ul className="nav-links">
              <li>
                <Link to="/" className={isActive("/") ? "active" : ""}>
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/oasis-1"
                  className={isActive("/oasis-1") ? "active" : ""}
                >
                  Oasis 1
                </Link>
              </li>
              <li>
                <Link
                  to="/oasis-2"
                  className={isActive("/oasis-2") ? "active" : ""}
                >
                  Oasis 2
                </Link>
              </li>
              <li>
                <Link
                  to="/about-us"
                  className={isActive("/about-us") ? "active" : ""}
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/contact-us"
                  className={isActive("/contact-us") ? "active" : ""}
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  to="/gallery"
                  className={isActive("/gallery") ? "active" : ""}
                >
                  Gallery
                </Link>
              </li>
              <li>
                <Link
                  to="/my-bookings"
                  className={isActive("/my-bookings") ? "active" : ""}
                >
                  My Bookings
                </Link>
              </li>
            </ul>
          </div>

          {/* PC View Component */}
          <PCView
            userData={userData}
            userRole={userRole}
            getAvatarSrc={getAvatarSrc}
            onViewProfile={() => setShowViewModal(true)}
            onEditProfile={handleEditProfile}
            onLogout={handleLogout}
          />

          {/* Mobile Toggle */}
          <button
            className={`navbar-toggle ${isOpen ? "active" : ""}`}
            onClick={() => setIsOpen(true)}
          >
            <span className="toggle-bar"></span>
            <span className="toggle-bar"></span>
            <span className="toggle-bar"></span>
          </button>
        </div>
      </nav>

      {/* Mobile View Component */}
      <MobileView
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        userData={userData}
        userRole={userRole}
        getAvatarSrc={getAvatarSrc}
        onViewProfile={() => setShowViewModal(true)}
        onEditProfile={handleEditProfile}
        onLogout={handleLogout}
      />

      {/* Logout Confirmation */}
      <LogoutConfirmModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={confirmLogout}
      />

      {/* Success/Error Message Toast */}
      {saveMessage && (
        <div className={`save-toast ${saveMessage.includes('success') ? 'success' : 'error'}`}>
          {saveMessage}
        </div>
      )}

      {/* Edit Profile Modal - Only ONE */}
      <EditProfileModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        userData={userData}
        getAvatarSrc={getAvatarSrc}
        onSave={handleSaveFromModal}
      />
    </>
  );
}

export default Navbar;